import { Box } from "@mui/material";
import { DotLegalLink } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import CircularProgressWithLabel from "@common/components/circularProgressWithLabel/CircularProgressWithLabel";
import RiskStatTile from "./RiskStatTile";
import { useRiskGovernanceOverviewStyles } from "./RiskGovernanceOverview.styles";
import { ControlCoverage } from "./RiskGovernanceOverview.utils";

interface RiskGovernanceStatRowProps {
    isLoading: boolean;
    isAsOf: boolean;
    afterMitigation: boolean;
    showExposureTile: boolean;
    assessedCount: number;
    // Already-resolved wording, since what the counts count depends on which subjects the area assesses.
    assessedTotalLabel: string;
    controlCoverageLabel: string;
    notAssessedCount: number;
    assessedProgress: number;
    appetite: number;
    overAppetiteCount: number;
    untreatedOverAppetiteCount: number;
    linkedRiskOverAppetiteCount: number;
    overduePlans: number;
    plansWithoutLinkedControls: number;
    controlCoverage: ControlCoverage;
    // The gap and headline deep links. All optional: the printed overview renders the same figures as plain text,
    // since a link in a PDF has nowhere to go.
    overAppetiteUrl?: string;
    untreatedOverAppetiteUrl?: string;
    linkedRiskOverAppetiteUrl?: string;
    overduePlansUrl?: string;
    plansWithoutLinkedControlsUrl?: string;
    notFullyCoveredUrl?: string;
    notAssessedUrl?: string;
    exposureTile: { value: string } | null;
}

function RiskGovernanceStatRow({
    isLoading,
    isAsOf,
    afterMitigation,
    showExposureTile,
    assessedCount,
    assessedTotalLabel,
    controlCoverageLabel,
    notAssessedCount,
    assessedProgress,
    appetite,
    overAppetiteCount,
    untreatedOverAppetiteCount,
    linkedRiskOverAppetiteCount,
    overduePlans,
    plansWithoutLinkedControls,
    controlCoverage,
    overAppetiteUrl,
    untreatedOverAppetiteUrl,
    linkedRiskOverAppetiteUrl,
    overduePlansUrl,
    plansWithoutLinkedControlsUrl,
    notFullyCoveredUrl,
    notAssessedUrl,
    exposureTile,
}: RiskGovernanceStatRowProps) {
    const styles = useRiskGovernanceOverviewStyles();
    const { translateString, translateNumber } = useTranslation();

    // Base tiles: assessed, over-appetite, linked risk, overdue plans, control coverage (5). The exposure tile adds
    // a sixth when the customer measures exposure.
    const statColumnCount = showExposureTile ? 6 : 5;

    // Named once for every figure read off current risk levels only: control coverage (control links carry no
    // mitigation projection and are not historized), linked risk (a neighbour's risk has no projected or past
    // variant) and the over-appetite tile's untreated gap. Each would otherwise sit beside a headline counting
    // projected levels or a past snapshot and contradict it, so they are withheld in both alternative modes rather
    // than shown against the wrong frame. The per-tile tooltips below stay separate: only the reason differs.
    const currentLevelsUnavailable = isAsOf || afterMitigation;

    const coverageTooltip = isAsOf
        ? translateString("riskGovernanceNotAvailableForPastDates")
        : afterMitigation
          ? translateString("riskGovernanceNotAvailableAfterMitigation")
          : translateString("riskGovernanceControlCoverageTooltip");

    const linkedRiskTooltip = isAsOf
        ? translateString("riskGovernanceNotAvailableForPastDates")
        : afterMitigation
          ? translateString("riskGovernanceLinkedRiskNotAvailableAfterMitigation")
          : translateString("riskGovernanceLinkedRiskTooltip");

    const renderGapLink = (to: string | undefined, text: string) =>
        to ? (
            <DotLegalLink to={to} linkColor="primary" noUnderlineOnHover>
                <Box component="span" sx={styles.gapLink}>
                    {text}
                </Box>
            </DotLegalLink>
        ) : (
            <Box sx={styles.statSubLabel}>{text}</Box>
        );

    // The headline figure: a red deep link when there is something to look at, otherwise the bare number, and a dash
    // when the tile's mode makes the figure meaningless. Shared by the three tiles that carry a count worth chasing,
    // so the stat-number styling triple is stated once.
    const renderStatNumber = (count: number, to: string | undefined, unavailable = false) =>
        !unavailable && to && count > 0 ? (
            <DotLegalLink to={to} linkColor="primary" noUnderlineOnHover>
                <Box sx={[styles.statNumber, styles.statNumberDanger, styles.statNumberLink]}>{translateNumber(count)}</Box>
            </DotLegalLink>
        ) : (
            <Box sx={[styles.statNumber, !unavailable && count > 0 && styles.statNumberDanger]}>{unavailable ? "—" : translateNumber(count)}</Box>
        );

    return (
        <Box sx={[styles.statRow, showExposureTile && styles.statRowWithExposure]}>
            <RiskStatTile
                label={translateString("riskGovernanceAssessed")}
                waveIndex={0}
                columnCount={statColumnCount}
                subLabel={
                    <Box>
                        <Box sx={styles.statSubLabel}>{assessedTotalLabel}</Box>
                        {notAssessedCount > 0 && renderGapLink(notAssessedUrl, translateString("riskGovernanceNotAssessed", { count: notAssessedCount }))}
                    </Box>
                }
                topRight={<CircularProgressWithLabel value={assessedProgress} size={44} variant="onLight" />}
                isLoading={isLoading}
            >
                <Box sx={styles.statNumber}>{translateNumber(assessedCount)}</Box>
            </RiskStatTile>
            <RiskStatTile
                label={translateString("riskGovernanceOverAppetite")}
                waveIndex={1}
                columnCount={statColumnCount}
                subLabel={
                    <Box>
                        <Box sx={styles.statSubLabel}>{translateString("riskGovernanceAppetiteHint", { appetite })}</Box>
                        {/* The gap under the headline: of the entities it counts, the ones whose over-appetite risk
                            carries no treatment decision at all. Risk-scoped, so it is a subset of the headline by
                            construction. Hidden in both alternative modes, where the headline counts projected
                            levels or a past snapshot and a current-level gap beneath it would contradict it. */}
                        {!currentLevelsUnavailable &&
                            untreatedOverAppetiteCount > 0 &&
                            renderGapLink(
                                untreatedOverAppetiteUrl,
                                translateString("riskGovernanceUntreatedOverAppetite", { count: untreatedOverAppetiteCount })
                            )}
                    </Box>
                }
                isLoading={isLoading}
            >
                {/* The number deep-links to the operational table filtered to exactly the entities it counts.
                    No link in after-mitigation or point-in-time mode, where the count and the live filter
                    would disagree (the hook resolves the url to undefined there). The headline itself still
                    renders in every mode, so it is never dashed. */}
                {renderStatNumber(overAppetiteCount, overAppetiteUrl)}
            </RiskStatTile>
            <RiskStatTile
                label={translateString("linkedRiskOverAppetite")}
                waveIndex={2}
                columnCount={statColumnCount}
                disabled={currentLevelsUnavailable}
                tooltip={linkedRiskTooltip}
                isLoading={!currentLevelsUnavailable && isLoading}
            >
                {/* Entities within appetite themselves (unassessed included) whose linked entities exceed it.
                    The number deep-links to the operational table with the exact filter combo that counts it
                    (Over appetite = Nej + Linked risk over appetite = Ja); no link when unavailable. */}
                {renderStatNumber(linkedRiskOverAppetiteCount, linkedRiskOverAppetiteUrl, currentLevelsUnavailable)}
            </RiskStatTile>
            <RiskStatTile
                label={translateString("riskGovernanceOverduePlans")}
                waveIndex={3}
                columnCount={statColumnCount}
                // Both figures read today's plans (a deadline is overdue now whatever date the matrix is read at),
                // so a past snapshot would put a live number under a historical heading. Disabled there instead.
                disabled={isAsOf}
                tooltip={isAsOf ? translateString("riskGovernanceNotAvailableForPastDates") : translateString("riskGovernanceOverduePlansTooltip")}
                subLabel={
                    // Plans that themselves link no controls. Plan-scoped like the headline, but an overlapping
                    // population rather than a subset of it, so it is phrased as its own count rather than a share.
                    !isAsOf &&
                    plansWithoutLinkedControls > 0 &&
                    renderGapLink(
                        plansWithoutLinkedControlsUrl,
                        translateString("riskGovernancePlansWithoutControlCoverage", { count: plansWithoutLinkedControls })
                    )
                }
                isLoading={!isAsOf && isLoading}
            >
                {/* Both figures on this tile count plans and land on the mitigation table, so unlike the entity
                    tiles the headline stays live in after-mitigation mode: a deadline does not move with the way
                    the matrix is being read. */}
                {renderStatNumber(overduePlans, overduePlansUrl, isAsOf)}
            </RiskStatTile>
            <RiskStatTile
                label={translateString("riskGovernanceControlCoverage")}
                waveIndex={4}
                columnCount={statColumnCount}
                disabled={currentLevelsUnavailable}
                tooltip={coverageTooltip}
                subLabel={
                    // The fraction always accompanies the percentage: a percentage off a small denominator
                    // misleads on its own. Below it, the red band, deep-linking to the operational table
                    // filtered by the same SQL fragment that counts it, so the figure equals the rows it lands
                    // on. An empty denominator gets its own sub-line — nothing over appetite is good news, not
                    // a data gap.
                    !currentLevelsUnavailable &&
                    (controlCoverage.total > 0 ? (
                        <Box>
                            <Box sx={styles.statSubLabel}>{controlCoverageLabel}</Box>
                            {controlCoverage.notFullyCovered > 0 &&
                                renderGapLink(notFullyCoveredUrl, translateString("riskGovernanceNotFullyCovered", { count: controlCoverage.notFullyCovered }))}
                        </Box>
                    ) : (
                        <Box sx={styles.statSubLabel}>{translateString("riskGovernanceNoRisksOverAppetite")}</Box>
                    ))
                }
                isLoading={!currentLevelsUnavailable && isLoading}
            >
                <Box sx={styles.statNumber}>
                    {currentLevelsUnavailable || controlCoverage.percentage === null ? "—" : `${translateNumber(controlCoverage.percentage)}%`}
                </Box>
            </RiskStatTile>
            {showExposureTile && (
                <RiskStatTile
                    label={translateString("riskExposureSettingsHeader")}
                    waveIndex={5}
                    columnCount={statColumnCount}
                    disabled={isAsOf}
                    tooltip={isAsOf ? translateString("riskGovernanceNotAvailableForPastDates") : undefined}
                    isLoading={!isAsOf && isLoading}
                >
                    <Box sx={styles.statNumber}>{isAsOf ? "—" : (exposureTile?.value ?? "—")}</Box>
                </RiskStatTile>
            )}
        </Box>
    );
}

export default RiskGovernanceStatRow;
